import { useEffect, useRef, useState, useCallback } from "react";
import { useGlobalState } from "../contexts/GlobalStateContext";
import MessageItem from "./MessageItem";
import { debounce } from "lodash";
import { createPortal } from "react-dom";
import { useWebSocketListener } from "./WebSocketService";
import { fetchUsername } from "../Helpers/utils";
import { useNavigate } from "react-router-dom";
import useScrollLockOnMount from "../Helpers/utils";
import SimpleConfirmation from "./SimpleConfirmation";

export default function MessageModal({ convo, onClose, setConversations }) {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const { globalState, updateNotificationCount, logOut, resetGlobalState, setShowNotifications } =
    useGlobalState();
  const {
    conversation_id: conversationId,
    user_id: receiverId,
    fullName: receiverName,
  } = convo;

  const [newMessage, setNewMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockStatus, setBlockStatus] = useState(null);

  // Refs for elements we need to interact with
  const messagesRef = useRef(null);
  const scrollAnchorRef = useRef(null);
  const messageInputRef = useRef(null);
  const messagesContentRef = useRef(null);

  const currentUserId = globalState.user.user_id;

  const firstFocusDone = useRef(false); // <-- NEW: To track if first focus has happened


  // Handle WebSocket for incoming messages (unchanged)
  useWebSocketListener((data) => {
    if (
      data.type === "new_message" &&
      data.conversation_id === conversationId
    ) {
      const messageWithFullName = {
        ...data,
        fullName: receiverName,
        created_at: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, messageWithFullName]);

      requestAnimationFrame(() => {
        scrollAnchorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      });

      setConversations((prevConversations) => {
        const filtered = prevConversations.filter(
          (c) => c.conversation_id !== data.conversation_id
        );
        return [
          {
            conversation_id: data.conversation_id,
            user_id: data.sender_id,
            fullName: receiverName,
            last_message: data.content,
            has_unread: true,
          },
          ...filtered,
        ];
      });
    }
  });

  // Effect for initial message load (unchanged)
  useEffect(() => {
    if (conversationId) {
      setMessages([]);
      setPage(1);
      setHasMore(true);
      loadMessages(1, true);
      updateNotificationCount();
    }
  }, [conversationId]);

  const pageRef = useRef(page); // Keep page in a ref for scroll handler (unchanged)

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Add this ref to track the loading state without causing re-renders
const isLoadingRef = useRef(loading);
useEffect(() => {
  isLoadingRef.current = loading;
}, [loading]);

// Modified effect for infinite scrolling
useEffect(() => {
  const container = messagesRef.current;
  if (!container) return;

  const handleScroll = debounce(async () => {
    const nearTop = container.scrollTop < 200;

    // Use the ref to prevent multiple fetches
    if (nearTop && hasMore && !isLoadingRef.current) {
      // --- 1. Store previous scroll state ---
      const prevScrollHeight = container.scrollHeight;
      const prevScrollTop = container.scrollTop;

      setLoading(true);

      const nextPage = pageRef.current + 1;
      const success = await loadMessages(nextPage);

      if (success) {
        setPage(nextPage);

        // --- 2. After DOM updates, apply the new scroll position ---
        requestAnimationFrame(() => {
          const newScrollHeight = container.scrollHeight;
          // The new scroll position is the old position plus the height of the new content
          container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
        });
      }
      
      setLoading(false);
    }
  }, 200);

  container.addEventListener("scroll", handleScroll);

  return () => {
    handleScroll.cancel();
    container.removeEventListener("scroll", handleScroll);
  };
}, [hasMore]); 

  // loadMessages function (unchanged)
  async function loadMessages(p = 1, scrollToAnchor = false) {
    try {
      const res = await fetch(`${baseUrl}/php/load_messages.php`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversationId, page: p }),
      });
      const data = await res.json();

      if (data.status === "success") {
        if (p === 1) {
          setMessages(data.messages.reverse());
        } else {
          setMessages((prev) => [...data.messages.reverse(), ...prev]);
        }

        if (data.messages.length === 0) setHasMore(false);

        if (p === 1 && scrollToAnchor) {
          setTimeout(() => {
            scrollAnchorRef.current?.scrollIntoView({ block: "end" });
          }, 1000);
        }
        return true;
      } else {
        console.error(data.message);
        return false;
      }
    } catch (err) {
      console.error("Failed to load messages", err);
      return false;
    }
  }

  // sendMessage function (unchanged)
  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    const trimmed = newMessage.trim();
    const shortMessage =
      trimmed.length > 50
        ? trimmed.slice(0, 50).split(" ").slice(0, -1).join(" ") + "..."
        : trimmed;
    try {
      setLoading(true);
      const response = await fetch(`${baseUrl}/php/send_message.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversation_id: conversationId,
          content: newMessage.trim(),
          receiver: receiverId,
        }),
      });

      const responseText = await response.text();
console.log("Raw response from PHP:", responseText);
      const result = JSON.parse(responseText);
      console.log("Parsed response from PHP:", result);

      if (result.status === "success") {
        const tempId = Date.now();
        const newMsg = {
          message_id: tempId,
          sender_id: currentUserId,
          fullName: globalState.user.fullName,
          content: newMessage.trim(),
          created_at: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, newMsg]);
        setNewMessage("");

        requestAnimationFrame(() => {
        scrollAnchorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      });

        setConversations((prevConversations) => {
          const filtered = prevConversations.filter(
            (c) => c.conversation_id !== conversationId
          );
          return [
            {
              conversation_id: conversationId,
              user_id: receiverId,
              fullName: receiverName,
              last_message: shortMessage,
              has_unread: false,
            },
            ...filtered,
          ];
        });
      } else if (result.status === "blocked") {
        if (result.blocked_by_me) {
          setBlockStatus("blocked_by_me");
          setShowBlockDialog(true);
        } else {
          setBlockStatus("user_blocked");
          setShowBlockDialog(true);
        }
        return;
      } else if (result.status === "error") {
        logOut();
        resetGlobalState();
      } else {
        alert("Failed to send message due to restrictions between users.");
        onClose();
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
    }
  };

  // handleUnblock function (unchanged)
  const handleUnblock = async () => {
    try {
      const response = await fetch(`/php/unblock_messages.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blocked_id: receiverId }),
      });
      const data = await response.json();
      if (!data.status === "success"){
        alert(`Failed to unblock messages. Please try again.`);
      }
    } catch (err) {
      console.error(`Error unblocking messages:`, err);
    }
  };

  // handleKeyDown function (unchanged)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // handleCloseModal function (unchanged)
  const handleCloseModal = () => {
    updateNotificationCount();
    onClose();
  };

  // redirectToUserProfile function (unchanged)
  async function redirectToUserProfile(userId) {
    const username = await fetchUsername(userId);
    navigate(`/@${username}`);
    setShowNotifications(false);
    onClose();
  }

  const isMobile =
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  useScrollLockOnMount(); // Apply body scroll lock

  // --- START NEW/MODIFIED: Aggressive scroll-to-bottom on textarea focus for mobile keyboard ---
  const performAggressiveScroll = useCallback(() => {
    const messageContentElement = messagesContentRef.current;
    if (!messageContentElement) return;

    requestAnimationFrame(() => {
      messageContentElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      setTimeout(() => {
        messageContentElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 50); // Small delay for the double-scroll
    });
  }, []);

      // NEW: Programmatically focus and blur on mount to prime the browser
  useEffect(() => {
    if (!isMobile) return; // Only for mobile devices
    const inputElement = messageInputRef.current;

    if (inputElement) {
      // Small delay to ensure the modal is fully rendered before trying to focus
      const timer = setTimeout(() => {
        console.log("Programmatic focus/blur on mount to prime keyboard.");
        inputElement.focus();
        // A very brief delay before blurring, just enough for the browser to register focus
        setTimeout(() => {
          inputElement.blur();
        }, 10); // Very short delay before blur
      }, 50); // Small initial delay after mount

      return () => clearTimeout(timer);
    }
  }, [isMobile]); // Run only once on mount for mobile

  return (
    <>
      {showBlockDialog && (
        <SimpleConfirmation
          blockStatus={blockStatus}
          onClose={() => setShowBlockDialog(false)}
          onUnblock={handleUnblock}
        />
      )}

      {createPortal(
        <div className="messages-modal modal" style={{ display: "block" }}>
          <div
            className={
              isMobile ? "messages-content-mobile" : "messages-content"
            }
            ref={messagesContentRef}
          >
            {/* Header */}
            <div id="messages-header">
              <span
                id="messages-title"
                style={{ cursor: "pointer" }}
                onClick={() => redirectToUserProfile(receiverId)}
              >
                {receiverName}
              </span>
              <span className="close" onClick={handleCloseModal}>
                ×
              </span>
            </div>

            {/* Message List */}
            <div
              id="messages-section"
              className="modal-body"
              style={{ backgroundColor: "white", paddingTop: 0 }}
              ref={messagesRef}
            >
              {loading && <div className="loading-spinner">Loading older messages...</div>}
              {messages.map((msg, index) => (
                <MessageItem
                  key={msg.message_id}
                  message={msg}
                  index={index}
                  currentUserId={currentUserId}
                />
              ))}

              <div
                id="scroll-anchor"
                ref={scrollAnchorRef}
                style={{ height: "1px" }}
              />
              {!loading && messages.length === 0 && (
                <p>No messages in this conversation.</p>
              )}
            </div>

            {/* Footer */}
            <div className="comments-footer">
              <img
                className="profile_icon"
                id="commenter_icon"
                src={`profile_pics_icons/${globalState.user.user_id}.jpg`}
                height="42"
                width="42"
                alt="Your Icon"
              />
              <textarea
                className="fullTextbox"
                id="messageInput"
                placeholder="Type your message here..."
                rows={1}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                ref={messageInputRef}
              />
              <button id="sendMessage" className="save" onClick={sendMessage}>
                Send
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}